import { useEffect, useRef, useState } from 'react';

export default function Solution() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      title: 'Dictate From Anywhere',
      description: 'Courthouse steps. Driver\'s seat. Walking the dog. Speak your pleading and let AI handle the rest.',
      delay: '0ms'
    },
    {
      title: 'Court-Specific Templates',
      description: 'State. Federal. Your jurisdiction, your formatting, your local rules baked in.',
      delay: '200ms'
    },
    {
      title: 'Hallucination Safeguards',
      description: 'Every output checked against legal standards. Citations verified. No made-up cases. No phantom statutes.',
      delay: '400ms'
    },
    {
      title: 'Privilege-Safe Architecture',
      description: 'Your sensitive data is protected, processed, and deleted. We never store client information. Nothing to subpoena. Nothing to breach.',
      delay: '600ms'
    }
  ];

  return (
    <section
      id="features"
      ref={sectionRef}
      className="relative py-32 px-8 bg-white"
    >
      <div className="max-w-[1200px] mx-auto">
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <h2 className="text-[#09090b] text-[26px] sm:text-[40px] lg:text-[56px] font-bold leading-tight mb-6 px-2">
            HushLegal: A Mobile-First<br className="hidden sm:block" />
            <span className="sm:hidden"> </span>Breakthrough in Legal AI
          </h2>
          <p className="text-[#52525b] text-[15px] sm:text-[20px] max-w-[800px] mx-auto leading-relaxed px-2">
            We built HushLegal because lawyers deserve AI that works the way they do. Fast. Mobile. And obsessively protective of client data.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group bg-[#f5f0e8] p-6 sm:p-10 rounded-2xl border border-[#e8dcc8] hover:border-[#8B4513] transition-all duration-500 hover:scale-105 hover:shadow-[0_0_40px_rgba(139,69,19,0.15)] cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: isVisible ? feature.delay : '0ms' }}
            >
              <h3 className="text-[#09090b] text-[20px] sm:text-[24px] font-bold mb-4 group-hover:text-[#8B4513] transition-colors">
                {feature.title}
              </h3>
              <p className="text-[#52525b] text-[14px] sm:text-[16px] leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
